import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  CardHeader,
  Button,
  CardContent,
  Box,
} from '@mui/material';
import { connect } from 'react-redux';
import {
  deleteEventStatus,
  editEventStatus,
} from '@pingum/app-state-management/eventStatus/actions';

import User from '../../../User';
import TaskStatus from '../../../TaskStatus';
import Property from '../../../Property';
import UserMentionTextField from '../../../UserMentionTextField';
import { formatDateString } from '../../../../utils/dateHelper';
import { getUser } from '../../../../utils/userHelper';
import { getCommentStringFromMentionString } from '../../../../utils/mentionsHelper';
import HistoryActionsMenu from '../HistoryActionMenu';
import BarnUsersAutocomplete from '../../../Autocompletes/BarnUsers';
import { getBarnUsersForAssignmentString } from '../../../../utils/scheduleHelper';

const getStatusActionText = (item) => {
  if (item.completeEvent) {
    return 'completed workflow with status';
  } if (item.startEvent) {
    return 'created workflow with status';
  }
  return 'changed the status to';
};

export class HistoryItemComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      editMode: false,
      editComment: null,
      editAssignment: null,
    };
  }

  deleteEventStatus = () => {
    this.props.deleteEventStatus(this.props.item.id);
  }

  editEventStatus = () => {
    this.setState({ editMode: true });
  }

  editModeCancel = () => {
    this.setState({
      editMode: false,
      editComment: null,
      editAssignment: null,
    });
  }

  editModeSave = () => {
    if (this.state.editComment) {
      this.props.editEventStatus(this.props.item.id, {
        statusComment: this.state.editComment,
        assignment: this.state.editAssignment,
      });
    }
    this.setState({
      editMode: false,
      editComment: null,
      editAssignment: null,
    });
  }

  handleCommentChange = (e) => {
    this.setState({ editComment: e.target.value });
  }

  handleAssignmentChange = (newAssignment) => {
    this.setState({ editAssignment: newAssignment });
  }

  render() {
    const {
      permissions,
      item,
      classes,
      children,
      isDeletingEventStatus,
      isEditingEventStatus,
      hideActions,
      event,
    } = this.props;
    const {
      editMode,
      editComment,
      editAssignment,
    } = this.state;
    const cardClasses = [classes.card];
    const { status } = item;

    return (
      <div className={classes.container}>
        <div className={cardClasses.join(' ')}>
          <CardHeader
            classes={{
              root: classes.cardHeader,
              avatar: classes.cardAvatar,
              content: classes.cardHeaderContent,
            }}
            avatar={(
              <User
                user={getUser({
                  email: item.createdByEmail,
                  userFullName: item.createdByName,
                })}
                secondary={formatDateString(item.createdDate)}
                userAction={(
                  <span>
                    &nbsp;{getStatusActionText(item)} <strong>{item.status.name}</strong>
                    {
                      !!item.assignment
                        && (
                          <span>
                            &nbsp;and changed assignment
                          </span>
                        )
                    }
                    {
                      !!event
                        && (
                          <span>
                            &nbsp;for <strong>{event.name}</strong> <Typography variant="caption" className={classes.eventIdField}>(#{event.id})</Typography>
                          </span>
                        )
                    }
                  </span>
                )}
              />
            )}
            action={
              (permissions.admin || permissions.owner) && !hideActions
                ? (
                  <HistoryActionsMenu
                    isLoading={isDeletingEventStatus || isEditingEventStatus}
                    onDelete={this.deleteEventStatus}
                    onEdit={!editMode
                      ? this.editEventStatus
                      : undefined}
                  />
                )
                : undefined
            }
          />
          <CardContent className={classes.comment} classes={{ root: classes.commentLastChild }}>
            { !!item.statusComment && !editMode && (
              <div className={classes.body}>
                <Typography variant="body1" className={classes.commentText} component="div">
                  {getCommentStringFromMentionString(item.statusComment)}
                </Typography>
              </div>
            )}
            {
              editMode && (
                <div className={classes.body}>
                  <div className={classes.editContainer}>
                    <UserMentionTextField
                      value={editComment || item.statusComment}
                      label="Comment"
                      onChange={this.handleCommentChange}
                    />
                    <Box sx={{ mt: 1 }}>
                      <BarnUsersAutocomplete
                        label="Assigned User(s)"
                        id="barn-users-task-status-autocomplete-assignment"
                        multiple
                        size="small"
                        value={getBarnUsersForAssignmentString(editAssignment || item.assignment, {})}
                        onSelect={(e, newBarnUsers) => {
                        // take selected barn users and map to assignment syntax @[NAME](AUTH0_USER_ID)
                          this.handleAssignmentChange(newBarnUsers.map((bu) => (bu.id
                            ? `@[${bu.userFullName}](${bu.auth0UserId || bu.email})`
                            : `@[${bu.userFullName || bu}](${null})`)).join(''));
                        }}
                        limitTags={5}
                      />
                    </Box>
                    <div className={classes.buttonContainer}>
                      <Button
                        onClick={this.editModeCancel}
                        size="small"
                        className={classes.cancelButton}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={this.editModeSave}
                        disabled={!editComment || !editAssignment}
                        size="small"
                        className={classes.saveButton}
                        color="primary"
                        variant="contained"
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              )
  }
            <div className={classes.statusContainer}>
              <TaskStatus
                eventStatus={item}
                icon={status.icon}
                color={status.iconColor}
                hideComment
                hideDate
                archived={!!item.archived}
                showDuration={!item.completeEvent}
                completed={item.completeEvent}
              />
            </div>
            {
              !!item.assignment
                && (
                  <Property label="Assignment">
                    {
                      (item.assignment.split(/(?=@\[)/)
                        .map((user) => (
                          <User
                            key={user.auth0UserId}
                            chip
                            user={{
                              auth0UserId: user.match(/@\[.*\]\((.*)\)/)[1],
                              userFullName: (user && user.substr(2, user.indexOf('](') - 2)) || '',
                            }}
                          />
                        )))
                    }
                  </Property>
                )
            }
          </CardContent>
        </div>
        {
          children
        }
      </div>
    );
  }
}

HistoryItemComponent.propTypes = {
  classes: PropTypes.object,
  permissions: PropTypes.object,
  item: PropTypes.object,
  children: PropTypes.any,
  isDeletingEventStatus: PropTypes.bool,
  isEditingEventStatus: PropTypes.bool,
  deleteEventStatus: PropTypes.func,
  editEventStatus: PropTypes.func,
  hideActions: PropTypes.bool,
  event: PropTypes.object,
};

HistoryItemComponent.defaultProps = {
  classes: {},
  permissions: {},
  item: {},
  children: null,
  isDeletingEventStatus: false,
  isEditingEventStatus: false,
  deleteEventStatus: () => {},
  editEventStatus: () => {},
  hideActions: false,
  event: null,
};

export const mapStateToProps = (state) => ({
  permissions: state.user.permissions,
  isDeletingEventStatus: state.eventStatus.isDeletingEventStatus,
  isEditingEventStatus: state.eventStatus.isEditingEventStatus,
});

export const mapDispatchToProps = (dispatch) => ({
  deleteEventStatus: (id) => dispatch(deleteEventStatus(id)),
  editEventStatus: (id, request) => dispatch(editEventStatus(id, request)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(HistoryItemComponent);
